import React from 'react';
import { useSelector } from 'react-redux';
import { Redirect, useLocation } from 'react-router-dom';
import { DomainContext, UserContext } from './context';
import { FIRST_WELCOME_IDENTIFIER, Task } from './redux/SignUpFlow/types';

const LandingPageRoot = React.lazy(() => import('./LandingPages/LandingPageRoot'));

// Redirector handles base route calls "/". There are several different user types and apps to handle so this makes selecting the correct route easier to handle
const Redirector = () => {
    const user = React.useContext(UserContext);
    const domainContextValue = React.useContext(DomainContext);
    const domainUISetting = domainContextValue?.domainData?.domainUISetting;
    const [redirectPath, setRedirectPath] = React.useState<string | null>(null);
    const location = useLocation();

    const WelcomeValue = useSelector(
        (state: { signUpFlow: { tasks: Task[] } }) => state.signUpFlow.tasks,
    );

    const firstLoginTask = React.useMemo(
        () => WelcomeValue.filter((task) => task.identifier === FIRST_WELCOME_IDENTIFIER)[0],
        [WelcomeValue],
    );

    // biome-ignore lint/correctness/useExhaustiveDependencies: <explanation>
    React.useLayoutEffect(() => {
        let path = '';

        if (user.id) {
            const { userType } = user.activeClient.userClients;

            switch (true) {
                // this is for facebook. We need this for the always open review access they have
                case user.activeClient.id === 12:
                    path = '/dashboard';
                    break;
                case userType === 'user' && user.activeClient.onboardStep !== 'done':
                    path = '/onboarding-legacy';
                    break;
                case userType === 'ambassador':
                    path = `/${domainUISetting?.defaultRoute || 'home'}`;
                    break;
                case userType === 'user' && firstLoginTask?.completed === false:
                    path = '/recruit#contacts';
                    break;
                case userType === 'user' && user.activeClient.onboardStep === 'done':
                    path = '/contacts#applicants';
                    break;
                default:
                    path = '/404';
            }
        } else path = 'landing';
        setRedirectPath(path);
    }, [
        user,
        domainUISetting?.defaultRoute,
        location.pathname,
        location.search,
        firstLoginTask?.completed,
    ]);

    if (!redirectPath) return null;
    if (redirectPath === 'landing') return <LandingPageRoot />;
    return <Redirect to={redirectPath} />;
};

export default Redirector;
